import { useState } from 'react'
import { useAuthStore } from '@/stores/auth-store'
import {
  useMyBanSupportThread,
  useSendBanSupportUserMessage,
} from '@/hooks/use-ban-support-messages'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Textarea } from '@/components/ui/textarea'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'

export function BannedAccountScreen() {
  const user = useAuthStore((s) => s.user)
  const signOut = useAuthStore((s) => s.signOut)
  const banReason =
    user?.ban_reason?.trim() || 'Your account has been suspended. Contact support below if you believe this was a mistake.'

  const { data: messages, isLoading } = useMyBanSupportThread(!!user?.is_banned)
  const sendMessage = useSendBanSupportUserMessage()
  const [draft, setDraft] = useState('')

  const handleSend = async () => {
    const text = draft.trim()
    if (!text.length) return
    try {
      await sendMessage.mutateAsync(text)
      setDraft('')
      toast.success('Message sent to support')
    } catch {
      toast.error('Could not send your message')
    }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-background p-4">
      <Card className="w-full max-w-lg border-destructive/30 shadow-xl">
        <CardHeader className="space-y-2">
          <CardTitle className="text-xl text-destructive">Account suspended</CardTitle>
          <CardDescription className="text-base text-muted-foreground">
            You cannot use Comfyflow while this suspension is active. This is why it was issued:
          </CardDescription>
          <blockquote className="rounded-md border border-border bg-muted/50 px-3 py-2 text-sm text-foreground">
            {banReason}
          </blockquote>
          <p className="text-sm text-muted-foreground">
            You may message the moderation team below. Responses will appear here when staff reply.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="max-h-48 overflow-y-auto rounded-md border bg-muted/20 p-3 text-sm">
            {isLoading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : !messages?.length ? (
              <p className="text-muted-foreground">No messages yet. Send one to reach support.</p>
            ) : (
              <ul className="space-y-3">
                {messages.map((m) => (
                  <li key={m.id} className="rounded-md bg-background px-2 py-1.5 shadow-sm">
                    <div className="text-xs uppercase text-muted-foreground">
                      {m.sender === 'user' ? 'You' : 'Support'}
                      <span className="ml-2 font-normal normal-case">
                        {new Date(m.created_at).toLocaleString()}
                      </span>
                    </div>
                    <p className="whitespace-pre-wrap text-foreground">{m.body}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="space-y-2">
            <Textarea
              placeholder="Write a message to the moderation team…"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={3}
              disabled={sendMessage.isPending}
            />
            <div className="flex flex-wrap gap-2">
              <Button type="button" onClick={() => void handleSend()} disabled={sendMessage.isPending || !draft.trim()}>
                {sendMessage.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Send message
              </Button>
              <Button type="button" variant="outline" onClick={() => void signOut()}>
                Sign out
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
